import React, { useState, useEffect, useRef } from 'react';
import { 
  Building2, MapPin, Award, CheckCircle2, ArrowRight, ShieldCheck, 
  Download, Laptop, Globe, Users, TrendingUp, Sparkles, Activity, 
  Volume2, Ear, Eye, Brain, Compass, Layers, ChevronRight, ChevronLeft,
  FileText, Zap, BarChart3, Stethoscope, Video, Heart, RefreshCw,
  ExternalLink, Printer, Share2, Check, Maximize2, Minimize2, Play, Pause,
  Monitor, Smartphone, Tablet, ChevronDown, CheckCircle, Languages
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AbilityBrandLogo, ClarvosCTAButton } from './ClarvosBrand';
import { MP_DECK_EN, MP_DECK_HI, DeckData } from '../data/madhyaPradeshDeckData';

interface MadhyaPradeshPresentationProps {
  onNavigateToTab: (tab: any) => void;
  onSelectModule?: (moduleId: string) => void;
  onBackToFounder?: () => void;
}

// Icon mapper for modules
const ICON_MAP: Record<string, any> = {
  Volume2,
  Activity,
  Brain,
  Eye,
  Compass,
  FileText,
  Stethoscope,
  Users,
  Award,
  Layers,
  Building2,
  TrendingUp,
  Heart,
  RefreshCw,
  BarChart3,
  Sparkles,
  Zap,
  Video,
  Laptop,
  Ear,
  MapPin
};

export const MadhyaPradeshPresentation: React.FC<MadhyaPradeshPresentationProps> = ({
  onNavigateToTab,
  onSelectModule,
  onBackToFounder
}) => {
  const { i18n } = useTranslation();
  const [deckLang, setDeckLang] = useState<'hi' | 'en'>(() => {
    return i18n.language === 'hi' || i18n.language === 'mr' ? 'hi' : 'en';
  });

  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [selectedPillarFilter, setSelectedPillarFilter] = useState<string>('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const presentationContainerRef = useRef<HTMLDivElement>(null);

  // Synchronize with global language if changed externally
  useEffect(() => {
    if (i18n.language === 'hi') {
      setDeckLang('hi');
    }
  }, [i18n.language]);

  const deck: DeckData = deckLang === 'hi' ? MP_DECK_HI : MP_DECK_EN;
  const slides = deck.slides;

  // Keyboard Slide Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        setActiveSlide(prev => Math.min(slides.length - 1, prev + 1));
      } else if (e.key === 'ArrowLeft') {
        setActiveSlide(prev => Math.max(0, prev - 1));
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length, isFullscreen]);

  // AutoPlay Timer
  useEffect(() => {
    let interval: any = null;
    if (isAutoPlay) {
      interval = setInterval(() => {
        setActiveSlide(prev => (prev + 1) % slides.length);
      }, 7000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlay, slides.length]);

  const handleCopyLink = () => {
    const shareUrl = deckLang === 'hi' 
      ? 'https://abilityos.bugfreesolution.com/madhya-pradesh?lang=hi'
      : 'https://abilityos.bugfreesolution.com/madhya-pradesh';
    navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (presentationContainerRef.current?.requestFullscreen) {
        presentationContainerRef.current.requestFullscreen().catch(() => {});
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };

  const toggleLanguage = (lang: 'hi' | 'en') => {
    setDeckLang(lang);
    if (i18n.changeLanguage) {
      i18n.changeLanguage(lang);
    }
  };

  const filteredModules = selectedPillarFilter === 'all' 
    ? deck.modules 
    : deck.modules.filter(m => m.category.toLowerCase().includes(selectedPillarFilter) || m.tag.toLowerCase().includes(selectedPillarFilter));

  const currentSlide = slides[activeSlide];

  return (
    <div 
      ref={presentationContainerRef}
      className={`min-h-screen w-full flex flex-col justify-between transition-all select-none ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-[#0e0d10] text-white p-4 sm:p-6 overflow-y-auto' 
          : 'fixed inset-0 z-40 bg-[#0e0d10] text-white overflow-y-auto p-3 sm:p-5'
      }`}
    >
      {/* Top Floating Keynote Header & Control Island */}
      <div className="w-full max-w-[1700px] mx-auto mb-3 shrink-0">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 px-5 py-3 bg-[#18171c]/90 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg">
          
          <div className="flex items-center gap-3">
            <div 
              onClick={() => onNavigateToTab('landing')}
              className="cursor-pointer flex items-center gap-2 hover:opacity-90 transition"
              title="Return to AbilityOS Homepage"
            >
              <AbilityBrandLogo variant="light" size="sm" />
            </div>
            
            <div className="h-6 w-px bg-white/15 hidden sm:block" />

            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-xl bg-[#ffd64e] text-black font-black font-mono text-xs flex items-center justify-center shadow-sm">
                MP
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-md border border-emerald-500/30">
                    {deck.header.stateTag}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">
                    {deck.slideChrome.slideOfTotal.replace('{curr}', String(activeSlide + 1)).replace('{total}', String(slides.length))}
                  </span>
                </div>
                <div className="text-xs sm:text-sm font-black text-white tracking-tight">
                  {deck.header.stateSubtitle}
                </div>
              </div>
            </div>
          </div>

          {/* Presentation Deck Quick Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            
            {/* Bilingual Hindi / English Toggle Switch */}
            <div className="flex items-center bg-white/10 p-1 rounded-xl border border-white/15">
              <button
                type="button"
                onClick={() => toggleLanguage('hi')}
                className={`px-3 py-1 rounded-lg text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer ${
                  deckLang === 'hi'
                    ? 'bg-[#ffd64e] text-black shadow-sm font-black'
                    : 'text-white hover:text-[#ffd64e]'
                }`}
                title="हिंदी में प्रस्तुति देखें"
              >
                <span>🇮🇳</span>
                <span>हिंदी</span>
              </button>
              <button
                type="button"
                onClick={() => toggleLanguage('en')}
                className={`px-3 py-1 rounded-lg text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer ${
                  deckLang === 'en'
                    ? 'bg-[#ffd64e] text-black shadow-sm font-black'
                    : 'text-white hover:text-[#ffd64e]'
                }`}
                title="View Presentation in English"
              >
                <span>EN</span>
              </button>
            </div>

            {onBackToFounder && (
              <button
                onClick={onBackToFounder}
                className="px-3 py-1.5 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-black font-mono transition flex items-center gap-1.5 cursor-pointer border border-amber-300"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                <span>{deck.header.founderCockpitBtn}</span>
              </button>
            )}

            <button
              onClick={() => setIsAutoPlay(!isAutoPlay)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer ${
                isAutoPlay ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              title="Toggle Auto Slide Advance (7s)"
            >
              {isAutoPlay ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              <span>{isAutoPlay ? deck.header.autoplayOnBtn : deck.header.autoplayBtn}</span>
            </button>

            <button
              onClick={toggleFullscreen}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer"
              title="Toggle Fullscreen Deck Mode"
            >
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              <span>{isFullscreen ? deck.header.exitFullscreenBtn : deck.header.fullscreenBtn}</span>
            </button>

            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer"
            >
              {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copiedLink ? deck.header.copiedBtn : deck.header.shareBtn}</span>
            </button>

            <button
              onClick={() => window.print()}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold font-mono transition flex items-center gap-1.5 cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>{deck.header.printBtn}</span>
            </button>

            <ClarvosCTAButton
              text={deck.header.requestPilotBtn}
              onClick={() => onNavigateToTab('request_demo')}
              variant="yellow"
              size="sm"
            />
          </div>

        </div>
      </div>

      {/* ======================================================================= */}
      {/* 16:9 CINEMATIC PRESENTATION STAGE (KEYNOTE / SLIDE CANVAS)               */}
      {/* ======================================================================= */}
      <div className="w-full max-w-[1700px] mx-auto mb-4 flex-1 flex flex-col">
        <div className="bg-[#18171c] text-white rounded-[32px] shadow-2xl border border-white/10 relative overflow-hidden flex flex-col justify-between flex-1 min-h-[520px] lg:min-h-[640px] p-6 sm:p-10 lg:p-12">
          
          {/* Ambient Lighting Accents */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#ffd64e]/10 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

          {/* Slide Header Row */}
          <div className="flex items-center justify-between gap-4 pb-6 border-b border-white/10 relative z-10">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-[#ffd64e] text-black font-black font-mono text-sm flex items-center justify-center">
                {currentSlide.slideNum}
              </span>
              <div>
                <div className="text-[10px] font-mono tracking-widest text-[#ffd64e] font-bold uppercase">
                  {currentSlide.kicker}
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  {currentSlide.tag}
                </div>
              </div>
            </div>

            {/* Slide Navigation Dots */}
            <div className="hidden sm:flex items-center gap-2">
              {slides.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSlide(idx)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    activeSlide === idx 
                      ? 'w-7 bg-[#ffd64e]' 
                      : 'w-2 bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Jump to slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* Previous / Next Arrow Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveSlide(prev => Math.max(0, prev - 1))}
                disabled={activeSlide === 0}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer text-white"
                title="Previous Slide (Left Arrow)"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setActiveSlide(prev => Math.min(slides.length - 1, prev + 1))}
                disabled={activeSlide === slides.length - 1}
                className="w-10 h-10 rounded-xl bg-[#ffd64e] text-black hover:bg-[#ffe27a] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer font-bold"
                title="Next Slide (Right Arrow or Spacebar)"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Slide Main Body Content */}
          <div className="py-6 sm:py-8 relative z-10 space-y-6 max-w-5xl">
            
            <div className="space-y-3">
              <span className="inline-block px-3.5 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-mono text-[#ffd64e] font-bold">
                {currentSlide.subtitle}
              </span>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight">
                {currentSlide.title}
              </h2>
            </div>

            {/* Bullet Points with Keynote Checkmarks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              {currentSlide.bullets.map((bullet, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-start gap-3.5"
                >
                  <div className="w-6 h-6 rounded-full bg-[#ffd64e]/20 text-[#ffd64e] flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
                    {bullet}
                  </p>
                </div>
              ))}
            </div>

            {/* Slide Statistics Bar */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
              {currentSlide.stats.map((stat, idx) => (
                <div key={idx} className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                  <div className="text-lg sm:text-2xl font-black text-[#ffd64e] font-mono">
                    {stat.val}
                  </div>
                  <div className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider">
                    {stat.label}
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-slate-400 font-mono">
                    {stat.sub}
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Slide Deck Footer */}
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 font-mono relative z-10">
            <div className="flex items-center gap-2">
              <span className="text-[#ffd64e] font-bold">BugFree Solutions Pvt Ltd</span>
              <span>•</span>
              <span>{deck.slideChrome.companySubtitle}</span>
            </div>
            <div className="flex items-center gap-3">
              <span>{deck.slideChrome.useArrowsHint}</span>
              <button
                onClick={() => setShowThumbnails(!showThumbnails)}
                className="text-white hover:text-[#ffd64e] underline cursor-pointer"
              >
                {showThumbnails ? deck.slideChrome.hideThumbnails : deck.slideChrome.showThumbnails}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ======================================================================= */}
      {/* SLIDE THUMBNAIL TRAY (KEYNOTE / POWERPOINT STRIP)                        */}
      {/* ======================================================================= */}
      {showThumbnails && (
        <div className="w-full max-w-[1700px] mx-auto mb-4 shrink-0">
          <div className="bg-[#18171c]/90 rounded-2xl p-3 sm:p-4 border border-white/10 shadow-lg space-y-2.5">
            <div className="flex items-center justify-between text-slate-300">
              <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#ffd64e]">
                {deck.slideChrome.keynoteStripTitle}
              </span>
              <span className="text-[11px] text-slate-400 font-mono">{deck.slideChrome.keynoteStripSub}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
              {slides.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveSlide(idx);
                  }}
                  className={`p-2 rounded-xl border text-left transition flex flex-col justify-between h-20 cursor-pointer ${
                    activeSlide === idx 
                      ? 'bg-slate-900 text-white border-amber-400 shadow-md ring-2 ring-[#ffd64e]' 
                      : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span className={`text-[9px] font-mono font-bold ${activeSlide === idx ? 'text-[#ffd64e]' : 'text-slate-400'}`}>
                    SLIDE {s.slideNum}
                  </span>
                  <p className="text-[10px] font-bold line-clamp-2 leading-tight">
                    {s.title}
                  </p>
                  <span className="text-[8px] font-mono text-slate-400 truncate">
                    {s.tag}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================================= */}
      {/* SECTION: ALL 31 PRODUCTS CATALOG & INTERACTIVE LIVE LAUNCHER             */}
      {/* ======================================================================= */}
      <div className="max-w-7xl mx-auto mb-16 w-full">
        <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-clarvos-gray-300 shadow-sm space-y-6">
          
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-mono font-bold text-[#1c1b1f] uppercase tracking-wider bg-[#ffd64e] px-2.5 py-1 rounded-full">
                {deck.catalogSection.badge}
              </span>
              <h3 className="text-2xl font-black text-slate-900 mt-2">
                {deck.catalogSection.title}
              </h3>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                {deck.catalogSection.subtitle}
              </p>
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { id: 'all', label: deck.catalogSection.filterAll },
                { id: 'communication', label: deck.catalogSection.filterSpeech },
                { id: 'behavioral', label: deck.catalogSection.filterAutism },
                { id: 'pediatrics', label: deck.catalogSection.filterEarly },
                { id: 'rehab', label: deck.catalogSection.filterRehab },
                { id: 'compliance', label: deck.catalogSection.filterGov }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setSelectedPillarFilter(f.id)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition cursor-pointer ${
                    selectedPillarFilter === f.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Module Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
            {filteredModules.map((mod, index) => {
              const Icon = ICON_MAP[mod.iconName] || Activity;
              return (
                <div 
                  key={mod.id}
                  className="p-5 rounded-2xl bg-[#f9f6f0] border border-clarvos-gray-300 hover:border-slate-800 transition flex flex-col justify-between group"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-900 group-hover:bg-[#ffd64e] transition">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-mono uppercase bg-white px-2 py-0.5 rounded-full border border-slate-200 font-bold text-slate-600">
                        {mod.tag}
                      </span>
                    </div>

                    <div>
                      <div className="text-[11px] font-mono text-slate-400">MODULE #{index + 1}</div>
                      <h4 className="font-black text-base text-slate-900 leading-snug">{mod.name}</h4>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">
                      {mod.desc}
                    </p>
                  </div>

                  <div className="pt-4 mt-3 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-[11px] font-mono font-bold text-slate-500">{mod.category}</span>
                    <button
                      onClick={() => {
                        if (onSelectModule) {
                          onSelectModule(mod.id);
                        } else {
                          onNavigateToTab('modules');
                        }
                      }}
                      className="text-xs font-bold text-slate-900 hover:text-black flex items-center gap-1 group-hover:translate-x-0.5 transition cursor-pointer"
                    >
                      <span>{deck.catalogSection.launchDemoBtn}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* ======================================================================= */}
      {/* SECTION: 52-DISTRICT OPERATIONAL FOOTPRINT                               */}
      {/* ======================================================================= */}
      <div className="max-w-7xl mx-auto mb-16 w-full">
        <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-clarvos-gray-300 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-mono font-bold text-emerald-700 uppercase tracking-wider bg-emerald-100 px-2.5 py-1 rounded-full">
                {deck.geographySection.badge}
              </span>
              <h3 className="text-2xl font-black text-slate-900 mt-2">
                {deck.geographySection.title}
              </h3>
            </div>
            <div className="text-right text-xs font-mono text-slate-500">
              {deck.geographySection.coverageSub}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {deck.districts.map((dist, i) => (
              <div key={i} className="p-4 rounded-2xl bg-[#f9f6f0] border border-clarvos-gray-300 flex flex-col justify-between hover:shadow-md transition">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-extrabold text-base text-slate-900">{dist.name}</span>
                    <span className="text-[10px] font-mono font-bold bg-[#ffd64e]/30 text-slate-800 px-2 py-0.5 rounded-md">
                      {dist.ddrc}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">{dist.role}</p>
                </div>
                <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-500">{deck.geographySection.estimatedCwsn}</span>
                  <span className="font-bold text-slate-900">{dist.cwsn} {deck.geographySection.studentsSuffix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ======================================================================= */}
      {/* SECTION: EXECUTIVE PILOT REQUEST ACTION CARD                            */}
      {/* ======================================================================= */}
      <div className="max-w-4xl mx-auto text-center space-y-4 pb-8 w-full">
        <h3 className="text-2xl font-black text-white">
          {deck.ctaSection.title}
        </h3>
        <p className="text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          {deck.ctaSection.subtitle}
        </p>
        <div className="pt-2 flex items-center justify-center gap-4">
          <ClarvosCTAButton
            text={deck.ctaSection.buttonText}
            onClick={() => onNavigateToTab('request_demo')}
            variant="yellow"
            size="lg"
          />
        </div>
      </div>

    </div>
  );
};
